"use client";

import Image from "next/image";
import {
    oldbone1image,
    oldbone2image,
    oldbone3image,
    oldbonebgimage,
} from "@/lib/images";
import { useCallback, useEffect, useRef, useState } from "react";

const residenceSlides = [
    {
        id: 1,
        image: oldbone1image,
        alt: "Oceanfront estate exterior",
    },
    {
        id: 2,
        image: oldbone2image,
        alt: "Luxury residence with palm trees",
    },
    {
        id: 3,
        image: oldbone3image,
        alt: "Modern poolside residence",
    },
];

const LOOP_SLIDE_INTERVAL_MS = 3000;
const LOOP_SLIDE_TRANSITION_MS = 500;

/** Extended strip: c1,c2,c3,c1',c2',c3' — index 0..3 then seamless reset */
const LOOP_SLIDE_SLOTS = [...residenceSlides, ...residenceSlides];

export default function OldBonesNewVibesGdrDevelopment() {
    const trackRef = useRef<HTMLDivElement>(null);
    const sliderIndexRef = useRef(0);
    const [sliderIndex, setSliderIndex] = useState(0);
    const [sliderTransition, setSliderTransition] = useState(true);
    const [stepPx, setStepPx] = useState(0);

    const measureStep = useCallback(() => {
        const track = trackRef.current;
        const first = track?.querySelector<HTMLElement>("[data-residence-slide]");
        if (!track || !first) return;
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 24;
        setStepPx(first.offsetWidth + gap);
    }, []);

    useEffect(() => {
        measureStep();
        const track = trackRef.current;
        const first = track?.querySelector<HTMLElement>("[data-residence-slide]");
        if (!first) return;
        const ro = new ResizeObserver(() => measureStep());
        ro.observe(first);
        return () => ro.disconnect();
    }, [measureStep]);

    const advanceForward = useCallback(() => {
        const i = sliderIndexRef.current;
        if (i < 2) {
            const next = i + 1;
            sliderIndexRef.current = next;
            setSliderIndex(next);
        } else if (i === 2) {
            sliderIndexRef.current = 3;
            setSliderIndex(3);
        }
    }, []);

    useEffect(() => {
        const id = window.setInterval(advanceForward, LOOP_SLIDE_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [advanceForward]);

    useEffect(() => {
        if (sliderIndex !== 3) return;
        const t = window.setTimeout(() => {
            setSliderTransition(false);
            sliderIndexRef.current = 0;
            setSliderIndex(0);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setSliderTransition(true);
                });
            });
        }, LOOP_SLIDE_TRANSITION_MS);
        return () => window.clearTimeout(t);
    }, [sliderIndex]);

    const offsetPx = stepPx > 0 ? sliderIndex * stepPx : 0;

    return (
        <section className="relative w-full overflow-hidden bg-black xl:py-24 py-6 xl:px-20 px-6">
            {/* Background image */}
            <div className="pointer-events-none absolute inset-0">
                <Image
                    src={oldbonebgimage}
                    alt="GDR Development coastal background"
                    fill
                    priority
                    className="object-cover"
                />
                <div className="absolute inset-0 bg-black/64" />
            </div>

            <div className="relative mx-auto max-w-[1320px] flex flex-col items-start gap-12 xl:flex-row xl:items-center">
                {/* Left content */}
                <div className="xl:max-w-xl space-y-6 text-white xl:w-[30%] w-full">
                    <div className="space-y-3">
                        <h2 className="text-[28px] md:text-[32px] font-extrabold leading-tight  xl:max-w-[200px] w-full">
                            Old Bones,
                            New Vibes
                        </h2>
                    </div>
                    <p className="md:text-[20px] text-[18px] xl:leading-[34px] leading-[28px] text-white/80 font-thin">
                        At GDR Development, we build homes with heart and a focus on quality.
                        Whether we&apos;re restoring a historic gem or creating a brand-new
                        space, we make sure each project feels right at home in its environment.
                        We treat every residence like it&apos;s for our own family—no wasted
                        space, no cutting corners, and a dedicated team that sticks with us from
                        the first idea to the final touches.
                    </p>
                </div>

                {/* Right slider: 2 full + 1 half, auto-advance */}
                <div className="relative w-full xl:max-w-[800px] overflow-hidden xl:w-[70%]">
                    <div className="relative min-h-[260px] md:min-h-[280px] xl:min-h-[366px]">
                        <div
                            ref={trackRef}
                            className="flex items-center gap-6"
                            style={{
                                transform: `translateX(-${offsetPx}px)`,
                                transition: sliderTransition
                                    ? `transform ${LOOP_SLIDE_TRANSITION_MS}ms ease-out`
                                    : "none",
                            }}
                        >
                            {LOOP_SLIDE_SLOTS.map((slide, i) => (
                                <div
                                    key={`${slide.id}-${i}`}
                                    data-residence-slide
                                    className="relative h-[280px] sm:w-[350px] w-full shrink-0 rounded-[28px] xl:h-[366px] xl:w-[324px]"
                                >
                                    <Image
                                        src={slide.image}
                                        alt={slide.alt}
                                        fill
                                        className="rounded-[28px] object-cover border border-white"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
